import { Fragment, useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Input, Layout, Space, Tooltip, Table, Select, Dropdown, Typography, Divider, message, DatePicker, Modal } from 'antd';
import { conferenceOnceApi, getConferenceLivesListApi, delLiveOnceApi, liveSettingApi } from '@services/Conference';
import copy from 'copy-to-clipboard';
import QRCode from 'qrcode.react';
import config from '@config';
import LiveUrl from './components/LiveUrl';
import './index.less';
import { convertDateTimeRangeToLocalText } from '@utils/utils';
import { deleteConferenceCache } from '../../../services/Conference';
import dayjs from 'dayjs';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { CONFERENCE_WORKSPACE_PARENT_PATH, CONFERENCE_WORKSPACE_ROUTE_BASE } from '@/layouts/conferenceWorkspaceConfig';
const { Search } = Input;
const { Text } = Typography;

export default function LiveList() {
    const navigate = useNavigate();

    const params = useParams();
    const conferenceId = params?.id || 0; // 会议项目 ID
    const [conference, setConference] = useState(null);

    const [loading, setLoading] = useState(false);
    const [liveListData, setLiveListData] = useState([]);

    const [filter, setFilter] = useState(null);

    const [liveUrlDrawer, setLiveUrlDrawer] = useState(false);
    const [liveId, setLiveId] = useState(null);
    const [liveName, setLiveName] = useState(null);

    const [visibleLiveQrcode, setVisibleLiveQrcode] = useState(false);
    const [qrcodeLive, setQrcodeLive] = useState(null);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    // 获取会议项目信息
    useEffect(() => {
        conferenceOnceApi({ id: conferenceId }).then(res => {
            if (res.status_code === 200) {
                setConference(res.data);
            }
        });
    }, []);

    // 请求直播列表
    const fetchLiveListData = async () => {
        setLoading(true);
        const res = await getConferenceLivesListApi({ conferenceId });
        if (res?.status_code === 200) {
            setLiveListData(res?.data || []);
        }
        setLoading(false);
    };

    // 获取直播列表
    useEffect(() => {
        fetchLiveListData();
    }, []);

    // 刷新列表
    const refreshData = () => fetchLiveListData();

    // 检索直播名称
    const onSearchLiveName = value =>
        setFilter({
            ...filter,
            name: value,
        });

    // 选择直播状态
    const onChangeFilterLiveStatus = value =>
        setFilter({
            ...filter,
            liveStatus: value,
        });

    // 选择直播日期
    const onChangeFilterLiveTime = value =>
        setFilter({
            ...filter,
            liveDay: value,
        });

    useEffect(() => {
        setLiveListData(
            liveListData.map(live => {
                const liveDay = filter?.liveDay ? dayjs(filter.liveDay).format('YYYY-MM-DD') : null;
                const startDate = dayjs(live.startTime).format('YYYY-MM-DD');
                const endDate = dayjs(live.endTime).format('YYYY-MM-DD');

                let isInRange = false;
                if (liveDay) {
                    isInRange = liveDay >= startDate && liveDay <= endDate;
                }

                const statusCondition = filter?.liveStatus === undefined || filter?.liveStatus === live.status;
                const nameCondition = !filter?.name || live.name.includes(filter?.name);
                const dayCondition = !liveDay || isInRange;

                const hidden = !(statusCondition && nameCondition && dayCondition);
                return { ...live, hidden };
            }),
        );
    }, [filter]);

    // 切换直播状态
    const onSelectLiveStatus = (id, status) => {
        liveSettingApi({ id, status }).then(res => {
            if (res?.status_code === 200) {
                setLiveListData(liveListData.map(live => (live.id === liveId ? { ...live, status } : live)));
                message.success('切换直播状态成功');
            }
        });
    };

    // 删除直播
    const deleteLive = id => {
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: '是否确认删除当前直播',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                delLiveOnceApi({ id }).then(res => {
                    if (res.status_code === 200) {
                        setLiveListData(liveListData.filter(live => live.id !== id));
                        message.success('直播删除成功');
                    }
                });
            },
        });
    };

    const getLiveH5Url = live => `${config.HUIYI_DOMAIN}/h5/${conference?.slug}/lives/${live?.id}`;

    // 复制直播链接（H5 端）
    const copyLiveUrl = record => {
        copy(getLiveH5Url(record));
        message.success('直播链接复制成功');
    };

    const showLiveQrCode = record => {
        if (!conference?.slug || !record?.id) {
            message.warning('会议或直播信息不完整，无法生成二维码');
            return;
        }
        setQrcodeLive(record);
        setVisibleLiveQrcode(true);
    };

    const closeLiveQrcode = () => {
        setVisibleLiveQrcode(false);
        setQrcodeLive(null);
    };

    const getLiveQrcodeDownloadFileName = () => {
        const raw = `${qrcodeLive?.id || ''}_${qrcodeLive?.name || ''}`;
        const safe = raw.replace(/[/\\?%*:|"<>]/g, '_').trim();
        const base = safe.length > 80 ? safe.slice(0, 80) : safe;
        return `${base || String(qrcodeLive?.id || 'live')}.png`;
    };

    /** 移动端 Safari / 微信内置浏览器不支持 a[download]，需打开图片后长按保存 */
    const shouldOpenQrcodeImageToSave = () => {
        const ua = navigator.userAgent || '';
        return /iP(hone|od|ad)/.test(ua) || /MicroMessenger/i.test(ua);
    };

    const downloadLiveQRCode = event => {
        event?.preventDefault();
        if (!qrcodeLive?.id) {
            return;
        }
        const canvas = document.getElementById(`qrcode_live_${qrcodeLive.id}`);
        if (!canvas || typeof canvas.toDataURL !== 'function') {
            message.warning('二维码未就绪，请稍后重试');
            return;
        }

        const fileName = getLiveQrcodeDownloadFileName();

        canvas.toBlob(
            blob => {
                if (!blob) {
                    message.error('生成二维码图片失败');
                    return;
                }
                const blobUrl = URL.createObjectURL(blob);

                if (shouldOpenQrcodeImageToSave()) {
                    const opened = window.open(blobUrl, '_blank');
                    if (!opened) {
                        message.warning('请允许弹窗，或长按上方二维码图片保存');
                    } else {
                        message.info('请在新页面长按图片保存到相册');
                    }
                    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                    return;
                }

                const link = document.createElement('a');
                link.style.display = 'none';
                link.download = fileName;
                link.href = blobUrl;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            },
            'image/png',
            1,
        );
    };

    // 直播观看限制
    const gotoSetupLimit = record => navigate(`/conf/conferences/${conferenceId}/lives/${record?.id}/limit`);

    // 直播回放管理
    const gotoReplayList = record => navigate(`/conf/conferences/${conferenceId}/lives/${record?.id}/replays`, { state: { title: record.name } });

    // 直播评论管理
    const gotoCommentList = record => navigate(`/conf/conferences/${conferenceId}/lives/${record?.id}/comments`);

    // 预约报名数据
    const gotoSignUpList = record => navigate(`/conf/conferences/${conferenceId}/lives/${record?.id}/signup-data`);

    const clearConferenceLiveCache = async live => {
        const data = {
            type: 'conference',
            key: `conference_${conferenceId}_user_live_${live?.id}`,
        };
        const res = await deleteConferenceCache(data);
        if (res?.status_code === 200) {
            message.success('更新会议官网嘉宾列表成功！');
        }
    };

    // 更新直播详情缓存：废弃
    // const onClearConferenceLiveCache = live => {
    //     if (conferenceId) {
    //         Modal.confirm({
    //             title: '更新官网直播信息',
    //             icon: <i className="bi bi-exclamation-circle"></i>,
    //             width: 480,
    //             centered: true,
    //             content: <>
    //                 <p>为减轻性能压力，系统自动缓存数据，该功能将更新用户端数据，展现最新设定内容。</p>
    //                 <p><Typography.Text type="danger">会议进行期间谨慎更新！</Typography.Text></p>
    //             </>,
    //             onOk() {
    //                 clearConferenceLiveCache(live);
    //             },
    //             cancelText: '取消，暂不更新',
    //             okText: '知晓，确认更新'
    //         });
    //     }
    // };

    const columns = [
        { title: '直播ID', dataIndex: 'id', width: 60 },
        {
            title: '直播名称',
            dataIndex: 'name',
            width: 300,
            render: (_, record) => {
                return (
                    <>
                        <a onClick={() => window.open(`${config.HUIYI_DOMAIN}/web/${conference?.slug}/lives/${record?.id}`)}>{record.name}</a>
                        {!!record?.conferenceName && (
                            <>
                                <br />
                                <Text type="secondary">{`[YHYC#${record.hallsId}] ${record.conferenceName}`}</Text>
                            </>
                        )}
                    </>
                );
            },
        },
        {
            title: '直播时间',
            dataIndex: 'datetime',
            width: 200,
            render: (_, record) => convertDateTimeRangeToLocalText(record.startTime, record.endTime),
        },
        {
            title: '直播状态',
            dataIndex: 'status',
            width: 80,
            render: (_, record) => {
                return (
                    <Select defaultValue={record.status} size="small" onSelect={value => onSelectLiveStatus(record.id, value)}>
                        <Select.Option value={0}>未开始</Select.Option>
                        <Select.Option value={1}>直播中</Select.Option>
                        <Select.Option value={2}>已结束</Select.Option>
                    </Select>
                );
            },
        },
        {
            title: '显示状态',
            dataIndex: 'hidden',
            width: 80,
            render: (_, record) => (!record.conferenceSiteHidden ? '显示' : <Text type="warning">隐藏</Text>),
        },
        {
            title: '观看限制',
            dataIndex: 'lookCheck',
            width: 80,
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 160,
            fixed: 'right',
            render: (_, record) => {
                const items = [
                    { label: '复制直播链接', key: 'link', onClick: () => copyLiveUrl(record) },
                    { label: '直播二维码', key: 'qrcode', onClick: () => showLiveQrCode(record) },
                    { label: '观看限制', key: 'limit', onClick: () => gotoSetupLimit(record) },
                    { label: '回放管理', key: 'replays', onClick: () => gotoReplayList(record) },
                    { label: '评论管理', key: 'comments', onClick: () => gotoCommentList(record) },
                    // { label: '更新直播信息', key: 'comments', danger: true, onClick: () => onClearConferenceLiveCache(record) },
                    { label: '预约报名数据', key: 'signUp', onClick: () => gotoSignUpList(record) },
                    { label: '删除直播', key: 'delete', danger: true, onClick: () => deleteLive(record.id) },
                ];

                return (
                    <span className="operation-btns">
                        <Button size="small" type="link" onClick={() => navigate(`/conf/conferences/${conferenceId}/lives/${record.id}`)}>
                            编辑直播
                        </Button>
                        <Divider orientation="vertical" />
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                setLiveUrlDrawer(true);
                                setLiveId(record.id);
                                setLiveName(record.name);
                            }}
                        >
                            推拉流信息
                        </Button>
                        <Divider orientation="vertical" />
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </span>
                );
            },
        },
    ];

    useEffect(() => {
        document.title = '直播管理-医会议';
    }, []);

    return (
        <Fragment>
            <div className="page-container live-list-page">
                <PageHeader title="会议直播" breadcrumb={[{ label: '医会议', path: CONFERENCE_WORKSPACE_PARENT_PATH }, { label: conference?.name || '项目信息', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}` }, '会议直播']} />

                <div ref={tableRegionRef} className="live-list-table-region">
                    <div className="main-toolbar live-list-toolbar">
                        <div className="main-tools">
                            <Space>
                                <Button type="primary" onClick={() => navigate(`/conf/conferences/${conferenceId}/lives/new`)}>
                                    新增直播
                                </Button>

                                <Search placeholder="直播名称关键字" allowClear style={{ width: 250 }} onSearch={onSearchLiveName} />
                                <DatePicker placeholder="直播日期" onChange={onChangeFilterLiveTime} />
                                <Select placeholder="直播状态" value={filter?.liveStatus} style={{ width: 100 }} onChange={onChangeFilterLiveStatus} allowClear>
                                    <Select.Option value={0}>未开始</Select.Option>
                                    <Select.Option value={1}>直播中</Select.Option>
                                    <Select.Option value={2}>已结束</Select.Option>
                                </Select>
                            </Space>
                        </div>
                        <div className="sub-tools">
                            <Tooltip title="刷新列表">
                                <Button
                                    type="default"
                                    icon={
                                        <span className="anticon">
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </span>
                                    }
                                    onClick={refreshData}
                                />
                            </Tooltip>
                        </div>
                    </div>

                    <div className="live-list-table-wrap">
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                            rowKey={item => item.id}
                            columns={columns}
                            dataSource={liveListData.filter(live => !live.hidden)}
                            loading={loading}
                            bordered
                            size="medium"
                            pagination={false}
                            scroll={{
                                x: 1300,
                                ...(tableScrollY != null ? { y: tableScrollY } : {}),
                            }}
                        />
                    </div>
                </div>
            </div>

            <Modal title="下载直播二维码" centered open={visibleLiveQrcode} onCancel={closeLiveQrcode} footer={null} width={520}>
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    {conference?.slug && qrcodeLive?.id && <QRCode id={`qrcode_live_${qrcodeLive.id}`} size={300} value={getLiveH5Url(qrcodeLive)} />}
                    <div style={{ textAlign: 'center', paddingTop: 10 }}>
                        {qrcodeLive?.id && (
                            <a role="button" href="#" onClick={downloadLiveQRCode}>
                                下载直播二维码
                            </a>
                        )}
                    </div>
                </div>
            </Modal>

            <LiveUrl liveUrlDrawer={liveUrlDrawer} liveName={liveName} setLiveUrlDrawer={setLiveUrlDrawer} liveId={liveId} conferenceSlug={conference?.slug} />
        </Fragment>
    );
}
